import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./MockExams.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, qbankLabel, QBankCode } from "../utils/qbankProfile";

interface ExamHistory {
    id: string;
    status: "in_progress" | "completed" | "abandoned";
    started_at: string;
    completed_at: string | null;
    time_taken_seconds: number;
    total_questions: number;
    correct_count: number | null;
    specialty_filter?: string | null;
}

interface AccessStatus {
    has_access: boolean;
    in_progress_exam: { id: string; started_at: string; specialty_filter?: string | null } | null;
}

interface Specialty {
    slug: string;
    name: string;
    count: number;
    section?: string;
}

const QUESTION_COUNT_OPTIONS = [50, 100, 150, 200] as const;
type QuestionCount = (typeof QUESTION_COUNT_OPTIONS)[number];
const ALL_SPECIALTIES = "all";

const MockExams: React.FC = () => {
    const { bank: bankParam } = useParams<{ bank: string }>();
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
    const [history, setHistory] = useState<ExamHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [questionCount, setQuestionCount] = useState<QuestionCount>(50);
    const [specialties, setSpecialties] = useState<Specialty[]>([]);
    const [selectedSpecialty, setSelectedSpecialty] = useState(ALL_SPECIALTIES);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
    const totalAvailableQuestions = specialties.reduce((sum, specialty) => sum + specialty.count, 0);
    const selectedSpecialtyData = specialties.find((specialty) => specialty.slug === selectedSpecialty);
    const availableQuestionCount =
        selectedSpecialty === ALL_SPECIALTIES
            ? totalAvailableQuestions
            : selectedSpecialtyData?.count ?? 0;
    const validQuestionCounts = QUESTION_COUNT_OPTIONS.filter((count) => count <= availableQuestionCount);
    const canStartExam = validQuestionCounts.length > 0;

    useEffect(() => {
        if (user?.id) {
            loadData();
        } else {
            setLoading(false);
        }
    }, [user, bank]);

    useEffect(() => {
        if (validQuestionCounts.length > 0 && !validQuestionCounts.includes(questionCount)) {
            setQuestionCount(validQuestionCounts[validQuestionCounts.length - 1]);
        }
    }, [questionCount, validQuestionCounts]);

    const loadData = async () => {
        if (!user?.id) return;

        try {
            // Check access
            const accessRes = await authenticatedFetch(`${apiUrl}/api/mock-exams/access?user_id=${user.id}&bank=${bank}`);
            if (accessRes.ok) {
                const accessData = await accessRes.json();
                setAccessStatus(accessData);
            }

            // Load history
            const historyRes = await authenticatedFetch(`${apiUrl}/api/mock-exams/history?user_id=${user.id}&bank=${bank}`);
            if (historyRes.ok) {
                const historyData = await historyRes.json();
                setHistory(historyData);
            }

            const specialtiesRes = await authenticatedFetch(`${apiUrl}/api/qbank/specialties?bank=${bank}`);
            if (specialtiesRes.ok) {
                const specialtiesData = await specialtiesRes.json();
                setSpecialties(specialtiesData);
            }
        } catch (err) {
            console.error("Failed to load mock exam data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleStartExam = async () => {
        if (!user?.id) return;
        setCreating(true);

        try {
            const payload: {
                user_id: string;
                question_count: QuestionCount;
                specialty_slug?: string;
                bank_code: QBankCode;
            } = { user_id: user.id, question_count: questionCount, bank_code: bank };

            if (selectedSpecialty !== ALL_SPECIALTIES) {
                payload.specialty_slug = selectedSpecialty;
            }

            const res = await authenticatedFetch(`${apiUrl}/api/mock-exams`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                const exam = await res.json();
                navigate(`/qbank/${bank}/mock-exams/${exam.id}`);
            } else {
                const error = await res.json();
                (window as any).showNotification?.(error.error || "Failed to start exam", "error");
            }
        } catch (err) {
            console.error("Failed to create exam", err);
            (window as any).showNotification?.("Failed to start exam", "error");
        } finally {
            setCreating(false);
        }
    };

    const formatExamPool = (specialtyFilter?: string | null) => {
        return specialtyFilter || "All specialties";
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const remainMins = mins % 60;
            return `${hrs}h ${remainMins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    const getScoreClass = (correct: number, total: number) => {
        const percent = (correct / total) * 100;
        if (percent >= 70) return styles.scoreGood;
        if (percent >= 50) return styles.scoreMedium;
        return styles.scoreLow;
    };

    if (loading) {
        return (
            <div className={styles.pageContainer}>
                <Header />
                <main className={styles.mainContent}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner} />
                        <p>Loading...</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    const hasAccess = accessStatus?.has_access ?? false;
    const inProgressExam = accessStatus?.in_progress_exam;

    return (
        <div className={styles.pageContainer}>
            <SEO
                title={`Mock Exams | ${qbankLabel(bank)} | C-Shaped`}
                description={`Take ${bank.toUpperCase()} practice exams with 50, 100, 150, or 200 random questions.`}
                canonical={`https://c-shaped.com/qbank/${bank}/mock-exams`}
            />
            <Header />

            <main className={styles.mainContent}>
                {/* Hero Section */}
                <section className={styles.heroSection}>
                    <h1 className={styles.heroTitle}>Mock Exams</h1>
                    <p className={styles.heroSubtitle}>
                        Build a custom-length {bank.toUpperCase()} practice test from published questions
                    </p>
                </section>

                {/* Premium Gate for non-subscribers */}
                {!hasAccess && user && (
                    <section className={styles.premiumGate}>
                        <div className={styles.lockIcon}>🔒</div>
                        <h2>Premium Plus Feature</h2>
                        <p>
                            Mock exams are exclusively available for Premium Plus subscribers.
                            Upgrade now to unlock unlimited practice exams with detailed performance analytics.
                        </p>
                        <div className={styles.gateActions}>
                            <Link to="/pricing" className={styles.upgradeButton}>
                                Upgrade to Premium Plus →
                            </Link>
                            <Link to="/mock-exams/demo" className={styles.demoButton}>
                                Try 10 Questions Free
                            </Link>
                        </div>
                    </section>
                )}

                {/* Guest CTA */}
                {!user && (
                    <section className={styles.premiumGate}>
                        <div className={styles.lockIcon}>📝</div>
                        <h2>Try a Free Mini Mock</h2>
                        <p>
                            Take 10 timed SDLE questions and review your score, explanations, and references. No account required.
                        </p>
                        <Link to="/mock-exams/demo" className={styles.upgradeButton}>
                            Start Free Mock →
                        </Link>
                    </section>
                )}

                {/* Start Exam Section (for Premium Plus users) */}
                {hasAccess && (
                    <section className={styles.startSection}>
                        {inProgressExam ? (
                            <div className={`${styles.startCard} ${styles.resumeCard}`}>
                                <h2>📋 Exam In Progress</h2>
                                <p>
                                    You have an unfinished exam started on{" "}
                                    {formatDate(inProgressExam.started_at)}.
                                    Continue where you left off.
                                </p>
                                <Link
                                    to={`/qbank/${bank}/mock-exams/${inProgressExam.id}`}
                                    className={`${styles.startButton} ${styles.resumeButton}`}
                                >
                                    Resume Exam →
                                </Link>
                            </div>
                        ) : (
                            <div className={styles.startCard}>
                                <h2>Start New Exam</h2>
                                <p>
                                    Choose a question pool and exam length. Questions are randomized
                                    from the published QBank.
                                </p>
                                <div className={styles.specialtyControl}>
                                    <div className={styles.questionCountHeader}>
                                        <span>Question pool</span>
                                        <small>{availableQuestionCount} available</small>
                                    </div>
                                    <select
                                        className={styles.specialtySelect}
                                        value={selectedSpecialty}
                                        onChange={(event) => setSelectedSpecialty(event.target.value)}
                                        disabled={creating}
                                    >
                                        <option value={ALL_SPECIALTIES}>
                                            All specialties ({totalAvailableQuestions})
                                        </option>
                                        {Object.entries(specialties.reduce<Record<string, Specialty[]>>((groups, specialty) => {
                                            const section = specialty.section || "Specialties";
                                            groups[section] = [...(groups[section] || []), specialty];
                                            return groups;
                                        }, {})).map(([section, items]) => (
                                            <optgroup key={section} label={section}>
                                                {items.map((specialty) => (
                                                    <option key={specialty.slug} value={specialty.slug}>
                                                        {specialty.name} ({specialty.count})
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.questionCountControl}>
                                    <div className={styles.questionCountHeader}>
                                        <span>Exam length</span>
                                        <small>{questionCount} questions selected</small>
                                    </div>
                                    <div className={styles.questionCountOptions} role="radiogroup" aria-label="Exam question count">
                                        {QUESTION_COUNT_OPTIONS.map((count) => (
                                            <button
                                                key={count}
                                                type="button"
                                                className={`${styles.questionCountOption} ${questionCount === count ? styles.questionCountOptionActive : ""}`}
                                                aria-pressed={questionCount === count}
                                                onClick={() => setQuestionCount(count)}
                                                disabled={creating || count > availableQuestionCount}
                                                title={count > availableQuestionCount ? "Not enough questions in this pool" : undefined}
                                            >
                                                <strong>{count}</strong>
                                                <span>questions</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {!canStartExam && (
                                    <p className={styles.unavailableMessage}>
                                        This pool has fewer than 50 published questions.
                                    </p>
                                )}
                                <button
                                    className={styles.startButton}
                                    onClick={handleStartExam}
                                    disabled={creating || !canStartExam}
                                >
                                    {creating ? "Starting..." : `Start ${questionCount}-Question Exam →`}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* History Section */}
                {hasAccess && (
                    <section className={styles.historySection}>
                        <h2>Exam History</h2>
                        {history.length === 0 ? (
                            <div className={styles.emptyHistory}>
                                <p>No exams taken yet. Start your first mock exam above!</p>
                            </div>
                        ) : (
                            <table className={styles.historyTable}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Pool</th>
                                        <th>Score</th>
                                        <th>Time</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((exam) => (
                                        <tr key={exam.id}>
                                            <td>{formatDate(exam.started_at)}</td>
                                            <td>
                                                <span
                                                    className={`${styles.statusBadge} ${exam.status === "completed"
                                                            ? styles.statusCompleted
                                                            : exam.status === "in_progress"
                                                                ? styles.statusInProgress
                                                                : styles.statusAbandoned
                                                        }`}
                                                >
                                                    {exam.status === "in_progress"
                                                        ? "In Progress"
                                                        : exam.status.charAt(0).toUpperCase() +
                                                        exam.status.slice(1)}
                                                </span>
                                            </td>
                                            <td>{formatExamPool(exam.specialty_filter)}</td>
                                            <td
                                                className={`${styles.scoreCell} ${exam.correct_count !== null
                                                        ? getScoreClass(
                                                            exam.correct_count,
                                                            exam.total_questions
                                                        )
                                                        : ""
                                                    }`}
                                            >
                                                {exam.correct_count !== null
                                                    ? `${exam.correct_count}/${exam.total_questions} (${Math.round(
                                                        (exam.correct_count / exam.total_questions) *
                                                        100
                                                    )}%)`
                                                    : "—"}
                                            </td>
                                            <td>
                                                {exam.time_taken_seconds > 0
                                                    ? formatDuration(exam.time_taken_seconds)
                                                    : "—"}
                                            </td>
                                            <td>
                                                {exam.status === "completed" ? (
                                                    <Link
                                                        to={`/qbank/${bank}/mock-exams/${exam.id}/results`}
                                                        className={styles.viewButton}
                                                    >
                                                        View Results →
                                                    </Link>
                                                ) : exam.status === "in_progress" ? (
                                                    <Link
                                                        to={`/qbank/${bank}/mock-exams/${exam.id}`}
                                                        className={`${styles.viewButton} ${styles.resumeLink}`}
                                                    >
                                                        Resume →
                                                    </Link>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default MockExams;
